import { EDisplays } from '@/context/display';
import { store } from '@/context/store';
import { useCopy } from '@/copy';

export const testId = 'ProgressBar';

export function ProgressBar(): JSX.Element {
    const { progressSteps, progressStepMap, segmentedProgress } = useCopy();
    // whichDisplayIsActive returns the journey phase, which is not the same as
    // the step shown when a partner collapses four phases into three.
    const activeStep = progressStepMap[whichDisplayIsActive()];

    return (
        <>
            <div
                className={`w-full _flex mb-1 pt-2 ${
                    segmentedProgress ? 'gap-2' : ''
                }`}
                data-testid={testId}
            >
                {progressSteps.map((name, index) => (
                    <div
                        key={index}
                        className={`${
                            segmentedProgress ? 'text-xs pb-1' : 'text-sm'
                        } font-sans w-full _flex justify-around flex-center   ${
                            activeStep === index
                                ? 'font-bold border-primary border-b-4 text-black'
                                : 'border-grey  border-b-2 text-textBody font-light'
                        }`}
                        style={{ whiteSpace: 'pre-wrap' }}
                    >
                        {name}
                    </div>
                ))}
            </div>
        </>
    );
}

/**
 * The journey's four phases, by name.
 *
 * Deliberately not the partner's own step labels: this feeds parentScreen on
 * every tracking event, so letting it follow partner copy would stop one
 * partner's analytics lining up with another's.
 */
const progressBarSections = [
    { name: 'Payment\nEstimate' },
    { name: 'Your\nDetails' },
    { name: 'Your\nQuote' },
    { name: 'Apply\nto lender' },
];

// Necessary for unique id
export function getActiveProgressText(): string {
    const activeDisplayIndex = whichDisplayIsActive();
    if (!activeDisplayIndex) return '';
    const index = progressBarSections[activeDisplayIndex];
    if (!index) return '';
    const section = progressBarSections[activeDisplayIndex].name;
    if (section === undefined) return 'Test Drive';
    const formatted = section.replace(/\n/g, ' ');
    return formatted;
}

function whichDisplayIsActive(): number {
    const state = store.getState();
    const display = state.displayReducer.display;

    switch (display) {
        case EDisplays.QUICK_CALCULATOR:
        case EDisplays.TRUCKSTOP_LANDING_PAGE:
        case EDisplays.NO_LENDER_HOME:
            return 0;
        // BUSINESS belongs here because collecting the business's details is
        // still collecting the applicant's details. Without it that screen fell
        // to the default, which is not a step at all, so the bar highlighted
        // nothing while the customer was on it.
        case EDisplays.LOAN:
        case EDisplays.STORE_SELECTOR:
        case EDisplays.BASIC:
        case EDisplays.BUSINESS:
        case EDisplays.INCOME:
        case EDisplays.EXPENSE:
        case EDisplays.VEHICLE:
        case EDisplays.SELLER:
        case EDisplays.IDENTIFICATION:
        case EDisplays.OCR_SUMMARY:
        case EDisplays.PREPPROVAL_ANIMATION:
        case EDisplays.OCR_ANIMATION:
        case EDisplays.INDICATIVELY_DECLINED:
        case EDisplays.INDICATIVELY_REFERRED:
            return 1;
        case EDisplays.SERVICING_SUCCESS:
        case EDisplays.YOUR_SCORES:
        case EDisplays.INDICATIVELY_APPROVED:
        case EDisplays.SERVICING_FAILURE:
        case EDisplays.SERVICING_TECHNICAL_FAILURE:
            return 2;
        case EDisplays.EMPLOYMENT:
        case EDisplays.ASSETS_AND_LIABILITIES:
        case EDisplays.SUPPORTING_DOCS:
            return 3;
        case EDisplays.TEST_DRIVE_AGREEMENT:
        case EDisplays.TEST_DRIVE_OCR_SUMMARY:
        case EDisplays.TEST_DRIVE_READY:
            return 4;
        default:
            return 5;
    }
}
